import { useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { CITIES, REGIONS, cityById } from "../model/cities.ts";
import {
  DEFAULT_PLAN,
  GROUPS,
  HANDLED_ELSEWHERE,
  STANDARD_ITEMS,
  TIER_LABEL,
  summarise,
} from "../model/expenses.ts";
import type { CustomItem, ExpensePlan, Frequency, LineResult, Tier } from "../model/expenses.ts";
import { CRORE } from "../model/types.ts";
import { groupAmount, money, parseAmount, pct, rupees } from "../lib/format.ts";

const MAX_AMOUNT = 1 * CRORE; // per period, per line

const clampInt = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, Math.round(Number.isFinite(v) ? v : lo)));

function periodLabel(f: Frequency, everyYears?: number) {
  if (f === "monthly") return "/ month";
  if (f === "yearly") return "/ year";
  return `every ${everyYears ?? 1} yrs`;
}

/** Amount input that holds a draft while typing and commits on blur, like NumberField. */
function AmountInput({
  value,
  onCommit,
  label,
}: {
  value: number;
  onCommit: (v: number) => void;
  label: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => setDraft(null), [value]);
  const commit = () => {
    if (draft === null) return;
    const n = parseAmount(draft, "inr");
    setDraft(null);
    if (!Number.isFinite(n)) return;
    const next = Math.min(MAX_AMOUNT, Math.max(0, Math.round(n)));
    if (next !== value) onCommit(next);
  };
  return (
    <input
      className="amount"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      aria-label={label}
      title="Accepts 45000, 45,000, 45k or 4.5L"
      value={draft ?? groupAmount(value, "inr")}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function DeviationChip({ line }: { line: LineResult }) {
  if (line.deviation === null || line.standard === null) return null;
  if (line.standard === 0 && line.amount === 0) return null;
  if (line.standard === 0) return <span className="chip high">added</span>;
  const d = line.deviation;
  if (Math.abs(d) < 0.15) return <span className="chip ok">standard</span>;
  if (d > 0) return <span className="chip high">{pct(d, 0)} above</span>;
  return <span className="chip low">{pct(-d, 0)} below</span>;
}

interface Props {
  /** The plan lives in App so the landing summary and the print view can read it. */
  plan: ExpensePlan;
  setPlan: Dispatch<SetStateAction<ExpensePlan>>;
  onApply: (annualTotal: number, cityId: string) => void;
  /** True when the calculator is using this plan's total. */
  inUse: boolean;
  /** The lifestyle figure the calculator is using now. */
  calculatorAmount: number;
}

export function ExpensePlanner({ plan, setPlan, onApply, inUse, calculatorAmount }: Props) {

  const summary = useMemo(() => summarise(plan), [plan]);
  const city = cityById(plan.cityId);
  const update = (patch: Partial<ExpensePlan>) => setPlan((p) => ({ ...p, ...patch }));
  const setOverride = (id: string, v: number) =>
    setPlan((p) => ({ ...p, overrides: { ...p.overrides, [id]: v } }));
  const clearOverride = (id: string) =>
    setPlan((p) => {
      const next = { ...p.overrides };
      delete next[id];
      return { ...p, overrides: next };
    });

  const addCustom = () =>
    update({
      custom: [
        ...plan.custom,
        { id: `c${Date.now()}`, label: "", amount: 0, frequency: "yearly", everyYears: 5 },
      ],
    });
  const editCustom = (id: string, patch: Partial<CustomItem>) =>
    update({ custom: plan.custom.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const removeCustom = (id: string) => update({ custom: plan.custom.filter((c) => c.id !== id) });

  const overrideCount = Object.keys(plan.overrides).length;
  const vsStandard = summary.annualTotal / summary.standardTotal - 1;
  const itemsById = Object.fromEntries(STANDARD_ITEMS.map((i) => [i.id, i]));

  return (
    <div className="planner">
      <section className="planner-controls" aria-label="Household and city">
        <div className="field">
          <label htmlFor="p-city">City</label>
          <select id="p-city" value={plan.cityId} onChange={(e) => update({ cityId: e.target.value })}>
            {REGIONS.map((r) => (
              <optgroup key={r} label={r}>
                {CITIES.filter((c) => c.region === r).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="field">
          <span className="field-label" id="p-tier">Standard of living</span>
          <div className="segmented" role="radiogroup" aria-labelledby="p-tier">
            {(["comfortable", "fat", "ultra"] as Tier[]).map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={plan.tier === t}
                className={plan.tier === t ? "on" : undefined}
                onClick={() => update({ tier: t })}
              >
                {TIER_LABEL[t]}
              </button>
            ))}
          </div>
        </div>

        <div className="field small">
          <label htmlFor="p-adults">Adults</label>
          <select id="p-adults" value={plan.adults} onChange={(e) => update({ adults: +e.target.value })}>
            {[1, 2].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="field small">
          <label htmlFor="p-kids">Children</label>
          <select id="p-kids" value={plan.children} onChange={(e) => update({ children: +e.target.value })}>
            {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="field small">
          <label htmlFor="p-parents">Parents living with you</label>
          <select id="p-parents" value={plan.parentsLiveIn} onChange={(e) => update({ parentsLiveIn: +e.target.value })}>
            {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </section>

      <p className="planner-note">
        Standards are {TIER_LABEL[plan.tier].toLowerCase()}-tier figures priced for {city.name}, in
        today's rupees. Each line moves with the city by how local it is — a cook's wage follows
        local costs, a flight to Europe does not — which is why a town that is{" "}
        {Math.round(Math.abs(1 - city.index) * 100)}% {city.index <= 1 ? "cheaper" : "dearer"} overall
        changes this total by less.
      </p>

      <div className="tiles">
        <div className="tile primary">
          <div className="label">Your lifestyle spend</div>
          <div className="value">{money(summary.annualTotal)}<span> / yr</span></div>
          <div className="sub">{money(summary.monthlyTotal)} a month, including a {pct(plan.bufferPct, 1)} buffer.</div>
        </div>
        <div className="tile">
          <div className="label">Standard for this household</div>
          <div className="value">{money(summary.standardTotal)}</div>
          <div className="sub">
            {overrideCount === 0 && plan.custom.length === 0
              ? "You are using every standard value."
              : `You are ${pct(Math.abs(vsStandard), 0)} ${vsStandard >= 0 ? "above" : "below"} it, with ${overrideCount} line${overrideCount === 1 ? "" : "s"} changed and ${plan.custom.length} added.`}
          </div>
        </div>
      </div>

      {GROUPS.map((group) => {
        const lines = summary.lines.filter((l) => l.group === group);
        const groupTotal = lines.reduce((a, l) => a + l.annual, 0);
        return (
          <section key={group} className="expense-group">
            <header>
              <h3>{group}</h3>
              <span className="group-total">{money(groupTotal)} / yr</span>
            </header>
            <div className="rows">
              {lines.map((line) => {
                const overridden = plan.overrides[line.id] !== undefined;
                return (
                  <div key={line.id} className={overridden ? "row edited" : "row"}>
                    <div className="row-label">
                      <span>{line.label}</span>
                      {itemsById[line.id]?.note && <small>{itemsById[line.id].note}</small>}
                    </div>
                    <div className="row-standard">
                      <span className="k">Standard</span>
                      {rupees(line.standard ?? 0)} <span className="per">{periodLabel(line.frequency, line.everyYears)}</span>
                    </div>
                    <div className="row-input">
                      <AmountInput
                        label={`${line.label}, ${periodLabel(line.frequency, line.everyYears)}`}
                        value={line.amount}
                        onCommit={(v) => setOverride(line.id, v)}
                      />
                      <span className="per">{periodLabel(line.frequency, line.everyYears)}</span>
                    </div>
                    <div className="row-meta">
                      <DeviationChip line={line} />
                      {overridden && (
                        <button type="button" className="link" onClick={() => clearOverride(line.id)}>
                          Use standard
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <section className="expense-group custom">
        <header>
          <h3>Your additions</h3>
          <span className="group-total">
            {money(summary.lines.filter((l) => l.group === "Your additions").reduce((a, l) => a + l.annual, 0))} / yr
          </span>
        </header>
        {plan.custom.length === 0 && (
          <p className="empty">
            Anything the standard list does not cover — a second home, a pet, supporting a sibling,
            a hobby that costs real money.
          </p>
        )}
        <div className="rows">
          {plan.custom.map((c) => (
            <div key={c.id} className="row custom-row">
              <input
                type="text"
                className="custom-label"
                placeholder="What is it?"
                aria-label="Expense name"
                value={c.label}
                maxLength={60}
                onChange={(e) => editCustom(c.id, { label: e.target.value })}
              />
              <div className="row-input">
                <AmountInput label={`${c.label || "Custom expense"} amount`} value={c.amount} onCommit={(v) => editCustom(c.id, { amount: v })} />
              </div>
              <select
                aria-label="How often"
                value={c.frequency}
                onChange={(e) => editCustom(c.id, { frequency: e.target.value as Frequency })}
              >
                <option value="monthly">per month</option>
                <option value="yearly">per year</option>
                <option value="every-n-years">every few years</option>
              </select>
              {c.frequency === "every-n-years" && (
                <select
                  aria-label="Every how many years"
                  value={c.everyYears}
                  onChange={(e) => editCustom(c.id, { everyYears: clampInt(+e.target.value, 1, 20) })}
                >
                  {[2, 3, 4, 5, 6, 8, 10, 15, 20].map((n) => <option key={n} value={n}>every {n} yrs</option>)}
                </select>
              )}
              <button type="button" className="link danger" onClick={() => removeCustom(c.id)}>
                Remove
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="secondary" onClick={addCustom}>
          Add an expense
        </button>
      </section>

      <section className="planner-foot">
        <div className="field buffer">
          <label htmlFor="p-buffer">Buffer for what nobody remembers to list</label>
          <select id="p-buffer" value={plan.bufferPct} onChange={(e) => update({ bufferPct: +e.target.value })}>
            {[0, 0.05, 0.075, 0.1, 0.15, 0.2].map((b) => (
              <option key={b} value={b}>{pct(b, b === 0.075 ? 1 : 0)}</option>
            ))}
          </select>
          <small className="hint">
            {money(summary.buffer)} a year. First-year spending in a new city almost always runs above
            plan.
          </small>
        </div>

        <div className="handled">
          <strong>Not included here — the calculator models these separately</strong>
          <ul>
            {HANDLED_ELSEWHERE.map((h) => <li key={h}>{h}</li>)}
          </ul>
          <p>They have their own inflation rates, some far above general CPI, so folding them into one
            lifestyle number would understate how fast your costs grow.</p>
        </div>

        <div className="apply">
          <p className={`source ${inUse ? "in-use" : ""}`} role="status">
            {inUse
              ? "✓ The calculator is using this plan."
              : `The calculator is using ${money(calculatorAmount)} a year, set on the calculator rather than from this plan. Use the button below to switch.`}
          </p>
          <button type="button" onClick={() => onApply(Math.round(summary.annualTotal), plan.cityId)}>
            Use {money(summary.annualTotal)} a year in the calculator
          </button>
          <button
            type="button"
            className="link"
            onClick={() => update({ overrides: {}, custom: [], bufferPct: DEFAULT_PLAN.bufferPct })}
          >
            Reset everything to standard
          </button>
          <p className="hint">
            Sets the calculator's lifestyle spend and city to match this page. Your entries here are
            saved in this browser only.
          </p>
        </div>
      </section>
    </div>
  );
}
